import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import type { CallRecord } from '@/types';
import { 
  getTableFieldVisibilityConfig,
  updateTableFieldVisibilityConfig,
  FieldVisibilityConfig 
} from '@/services/supabase';

// Visibility contexts
export type VisibilityContext = 'table' | 'modal' | 'csv' | 'pdf';

// Field key type
export type CallRecordKey = keyof CallRecord;

// Field definition with metadata
export interface FieldDefinition {
  key: CallRecordKey;
  label: string;
  description?: string;
  category: 'basic' | 'metrics' | 'details' | 'system';
}

// All field definitions
export const FIELD_DEFINITIONS: FieldDefinition[] = [
  { key: 'id', label: 'ID', description: 'Unique record identifier', category: 'system' },
  { key: 'created_at', label: 'Date', description: 'Record creation date', category: 'basic' },
  { key: 'name', label: 'Name', description: 'Caller name', category: 'basic' },
  { key: 'phone_number', label: 'Phone', description: 'Phone number', category: 'basic' },
  { key: 'category', label: 'Category', description: 'Call category', category: 'details' },
  { key: 'service_type', label: 'Service', description: 'Type of service', category: 'details' },
  { key: 'inquiry', label: 'Inquiry', description: 'Customer inquiry', category: 'details' },
  { key: 'call_duration_sec', label: 'Duration', description: 'Call duration in seconds', category: 'metrics' },
  { key: 'status', label: 'Status', description: 'Resolution status', category: 'metrics' },
  { key: 'urgency', label: 'Urgency', description: 'Priority level', category: 'metrics' },
  { key: 'notes', label: 'Notes', description: 'Additional notes', category: 'details' },
  { key: 'call_summary', label: 'Summary', description: 'Call summary', category: 'details' },
  { key: 'conversation_id', label: 'Conversation ID', description: 'Conversation reference', category: 'system' },
  { key: 'audio_uri', label: 'Audio', description: 'Recording URL', category: 'system' },
];

// Visibility configuration type - now dynamic to support any table
export type VisibilityConfig = Record<string, Record<VisibilityContext, boolean>>;

// Default visibility settings
export const DEFAULT_VISIBILITY: VisibilityConfig = {
  id: { table: false, modal: false, csv: false, pdf: false },
  created_at: { table: true, modal: true, csv: true, pdf: true },
  name: { table: true, modal: true, csv: true, pdf: true },
  phone_number: { table: true, modal: true, csv: true, pdf: true },
  category: { table: true, modal: true, csv: true, pdf: true },
  service_type: { table: true, modal: true, csv: true, pdf: true },
  inquiry: { table: false, modal: true, csv: true, pdf: true },
  call_duration_sec: { table: true, modal: true, csv: true, pdf: true },
  status: { table: true, modal: true, csv: true, pdf: true },
  urgency: { table: true, modal: true, csv: true, pdf: true },
  notes: { table: false, modal: true, csv: true, pdf: true },
  call_summary: { table: false, modal: true, csv: true, pdf: true },
  conversation_id: { table: false, modal: true, csv: false, pdf: false },
  audio_uri: { table: false, modal: true, csv: false, pdf: false },
};

// Convert database config to VisibilityConfig type
const convertToVisibilityConfig = (dbConfig: FieldVisibilityConfig | null): VisibilityConfig => {
  if (!dbConfig) {
    return {};
  }

  // Convert the database config to our VisibilityConfig format
  const converted: VisibilityConfig = {};
  
  Object.keys(dbConfig).forEach((key) => {
    const contextConfig = dbConfig[key];
    if (contextConfig) {
      converted[key] = {
        table: contextConfig.table ?? false,
        modal: contextConfig.modal ?? false,
        csv: contextConfig.csv ?? false,
        pdf: contextConfig.pdf ?? false,
      };
    }
  });

  return converted;
};

// Context type
interface FieldVisibilityContextType {
  visibilityConfig: VisibilityConfig;
  updateFieldVisibility: (fieldKey: string, context: VisibilityContext, visible: boolean) => void;
  updateFieldVisibilityBatch: (updates: { fieldKey: string; context: VisibilityContext; visible: boolean }[]) => void;
  updateContextVisibility: (context: VisibilityContext, visible: boolean) => void;
  resetToDefaults: () => void;
  isFieldVisible: (fieldKey: string, context: VisibilityContext) => boolean;
  getVisibleFields: (context: VisibilityContext) => string[];
  isLoading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
  tableName: string;
  setTableName: (table: string) => void;
}

const FieldVisibilityContext = createContext<FieldVisibilityContextType | undefined>(undefined);

interface FieldVisibilityProviderProps {
  children: ReactNode;
  tableName?: string;
}

export function FieldVisibilityProvider({ children, tableName = 'alsalama_call_records' }: FieldVisibilityProviderProps) {
  const [visibilityConfig, setVisibilityConfig] = useState<VisibilityConfig>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTableName, setCurrentTableName] = useState(tableName);
  const fetchInProgressRef = useRef(false);

  // Sync currentTableName with tableName prop when it changes
  useEffect(() => {
    if (tableName !== currentTableName) {
      setCurrentTableName(tableName);
    }
  }, [tableName, currentTableName]);

  // Fetch visibility config for the specified table
  const fetchVisibilityConfig = useCallback(async () => {
    // Skip if fetch is already in progress
    if (fetchInProgressRef.current) {
      return;
    }
    
    fetchInProgressRef.current = true;
    
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await getTableFieldVisibilityConfig(currentTableName);

      if (fetchError) {
        // Ignore AbortError - request was cancelled
        if (fetchError.message && fetchError.message.includes('aborted')) {
          console.log('Fetch aborted, ignoring...');
          return;
        }
        console.error('Error fetching visibility config:', fetchError);
        setError('Failed to load visibility settings');
        // Fall back to empty config on error
        setVisibilityConfig({});
      } else if (data) {
        setVisibilityConfig(convertToVisibilityConfig(data));
      } else {
        // No config found for this table, use empty
        setVisibilityConfig({});
      }
    } catch (err) {
      // Ignore AbortError
      if (err && typeof err === 'object' && 'message' in err && 
          String(err.message).includes('aborted')) {
        console.log('Fetch aborted, ignoring...');
        return;
      }
      console.error('Error in fetchVisibilityConfig:', err);
      setError('Failed to load visibility settings');
      setVisibilityConfig({});
    } finally {
      setIsLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [currentTableName]);

  // Fetch on mount and when tableName changes
  useEffect(() => {
    fetchVisibilityConfig();
  }, [fetchVisibilityConfig]);

  // Update a single field's visibility for a specific context
  const updateFieldVisibility = useCallback(
    async (
      fieldKey: string,
      context: VisibilityContext,
      visible: boolean
    ) => {
      // Update local state immediately for better UX
      const updatedConfig = {
        ...visibilityConfig,
        [fieldKey]: {
          ...visibilityConfig[fieldKey],
          table: visibilityConfig[fieldKey]?.table ?? true,
          modal: visibilityConfig[fieldKey]?.modal ?? true,
          csv: visibilityConfig[fieldKey]?.csv ?? true,
          pdf: visibilityConfig[fieldKey]?.pdf ?? true,
          [context]: visible,
        },
      };
      
      setVisibilityConfig(updatedConfig);

      // Persist to database (for admin users)
      try {
        const dbConfig: FieldVisibilityConfig = {};
        Object.keys(updatedConfig).forEach((key) => {
          dbConfig[key] = {
            table: updatedConfig[key].table ?? false,
            modal: updatedConfig[key].modal ?? false,
            csv: updatedConfig[key].csv ?? false,
            pdf: updatedConfig[key].pdf ?? false,
          };
        });
        await updateTableFieldVisibilityConfig(currentTableName, dbConfig);
      } catch (err) {
        console.error('Error saving visibility config:', err);
      }
    },
    [visibilityConfig, currentTableName]
  );

  // Batch update multiple fields' visibility at once
  const updateFieldVisibilityBatch = useCallback(
    async (updates: { fieldKey: string; context: VisibilityContext; visible: boolean }[]) => {
      // Apply all updates to the config at once
      const updatedConfig = updates.reduce(
        (config, update) => ({
          ...config,
          [update.fieldKey]: {
            ...config[update.fieldKey],
            table: config[update.fieldKey]?.table ?? true,
            modal: config[update.fieldKey]?.modal ?? true,
            csv: config[update.fieldKey]?.csv ?? true,
            pdf: config[update.fieldKey]?.pdf ?? true,
            [update.context]: update.visible,
          },
        }),
        { ...visibilityConfig }
      );

      // Update local state once with all changes
      setVisibilityConfig(updatedConfig);

      // Persist to database only once with the complete config
      try {
        const dbConfig: FieldVisibilityConfig = {};
        Object.keys(updatedConfig).forEach((key) => {
          dbConfig[key] = {
            table: updatedConfig[key]?.table ?? false,
            modal: updatedConfig[key]?.modal ?? false,
            csv: updatedConfig[key]?.csv ?? false,
            pdf: updatedConfig[key]?.pdf ?? false,
          };
        });
        await updateTableFieldVisibilityConfig(currentTableName, dbConfig);
      } catch (err) {
        console.error('Error saving visibility config:', err);
      }
    },
    [visibilityConfig, currentTableName]
  );

  // Update all fields for a specific context
  const updateContextVisibility = useCallback(async (context: VisibilityContext, visible: boolean) => {
    const newConfig = { ...visibilityConfig };
    Object.keys(newConfig).forEach(key => {
      newConfig[key] = {
        ...newConfig[key],
        [context]: visible,
      };
    });
    setVisibilityConfig(newConfig);

    // Persist to database
    try {
      const dbConfig: FieldVisibilityConfig = {};
      Object.keys(newConfig).forEach((key) => {
        dbConfig[key] = {
          table: newConfig[key]?.table ?? false,
          modal: newConfig[key]?.modal ?? false,
          csv: newConfig[key]?.csv ?? false,
          pdf: newConfig[key]?.pdf ?? false,
        };
      });
      await updateTableFieldVisibilityConfig(currentTableName, dbConfig);
    } catch (err) {
      console.error('Error saving visibility config:', err);
    }
  }, [visibilityConfig, currentTableName]);

  // Reset to default settings - clears the config
  const resetToDefaults = useCallback(async () => {
    setVisibilityConfig({});
    
    // Persist empty config to database
    try {
      await updateTableFieldVisibilityConfig(currentTableName, {});
    } catch (err) {
      console.error('Error saving visibility config:', err);
    }
  }, [currentTableName]);

  // Check if a field is visible in a specific context
  const isFieldVisible = useCallback(
    (fieldKey: string, context: VisibilityContext): boolean => {
      return visibilityConfig[fieldKey]?.[context] ?? false;
    },
    [visibilityConfig]
  );

  // Get all visible fields for a specific context
  const getVisibleFields = useCallback(
    (context: VisibilityContext): string[] => {
      return Object.keys(visibilityConfig).filter(
        key => visibilityConfig[key]?.[context]
      );
    },
    [visibilityConfig]
  );

  // Handle table name change
  const handleSetTableName = useCallback((table: string) => {
    setCurrentTableName(table);
  }, []);

  return (
    <FieldVisibilityContext.Provider
      value={{
        visibilityConfig,
        updateFieldVisibility,
        updateFieldVisibilityBatch,
        updateContextVisibility,
        resetToDefaults,
        isFieldVisible,
        getVisibleFields,
        isLoading,
        error,
        refreshConfig: fetchVisibilityConfig,
        tableName: currentTableName,
        setTableName: handleSetTableName,
      }}
    >
      {children}
    </FieldVisibilityContext.Provider>
  );
}

export function useFieldVisibility() {
  const context = useContext(FieldVisibilityContext);
  if (context === undefined) {
    throw new Error('useFieldVisibility must be used within a FieldVisibilityProvider');
  }
  return context;
}
